/**
 * Custom Attributes Settings
 * Manage user-defined lead/client attribute categories and their options
 */

import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Popconfirm,
  message,
  Empty,
  ColorPicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customAttributeApi } from '../../ai-crm/api/customAttributeApi';
import type { CustomAttribute, CreateCustomAttributeInput, UpdateCustomAttributeInput } from '../../ai-crm/api/customAttributeApi';

const { Title, Text } = Typography;

const APPLIES_TO_OPTIONS = [
  { label: 'Leads & Clients', value: 'BOTH' },
  { label: 'Leads Only', value: 'LEAD' },
  { label: 'Clients Only', value: 'CLIENT' },
];

const PRESET_COLORS = [
  '#1677ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
  '#eb2f96', '#13c2c2', '#fa8c16', '#2f54eb', '#a0d911',
];

const CustomAttributesSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAttr, setEditingAttr] = useState<CustomAttribute | null>(null);
  const [form] = Form.useForm();
  const [options, setOptions] = useState<Array<{ label: string; value: string; color: string }>>([]);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionColor, setNewOptionColor] = useState('#1677ff');

  const { data: attributes = [], isLoading } = useQuery({
    queryKey: ['customAttributes'],
    queryFn: () => customAttributeApi.getAll(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateCustomAttributeInput) => customAttributeApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customAttributes'] });
      message.success('Attribute created');
      closeModal();
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomAttributeInput }) =>
      customAttributeApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customAttributes'] });
      message.success('Attribute updated');
      closeModal();
    },
    onError: (err: any) => message.error(err?.response?.data?.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customAttributeApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customAttributes'] });
      message.success('Attribute deleted');
    },
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingAttr(null);
    setOptions([]);
    setNewOptionLabel('');
    form.resetFields();
  };

  const openCreate = () => {
    setEditingAttr(null);
    setOptions([]);
    form.setFieldsValue({ name: '', appliesTo: 'BOTH', isRequired: false });
    setModalOpen(true);
  };

  const openEdit = (attr: CustomAttribute) => {
    setEditingAttr(attr);
    setOptions(attr.options.map(o => ({ label: o.label, value: o.value, color: o.color || '#1677ff' })));
    form.setFieldsValue({
      name: attr.name,
      appliesTo: attr.appliesTo,
      isRequired: attr.isRequired,
    });
    setModalOpen(true);
  };

  const addOption = () => {
    const label = newOptionLabel.trim();
    if (!label) return;
    if (options.some(o => o.label.toLowerCase() === label.toLowerCase())) {
      message.warning('Option already exists');
      return;
    }
    setOptions([...options, { label, value: label, color: newOptionColor }]);
    setNewOptionLabel('');
  };

  const removeOption = (idx: number) => {
    setOptions(options.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    if (options.length === 0) {
      message.warning('Add at least one option');
      return;
    }

    const payload = {
      name: values.name,
      appliesTo: values.appliesTo,
      isRequired: values.isRequired || false,
      options: options.map(o => ({ label: o.label, value: o.value, color: o.color })),
    };

    if (editingAttr) {
      updateMutation.mutate({ id: editingAttr.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const columns = [
    {
      title: 'Attribute Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Applies To',
      dataIndex: 'appliesTo',
      key: 'appliesTo',
      render: (val: string) => {
        const map: Record<string, { color: string; label: string }> = {
          BOTH: { color: 'blue', label: 'Leads & Clients' },
          LEAD: { color: 'green', label: 'Leads Only' },
          CLIENT: { color: 'purple', label: 'Clients Only' },
        };
        const item = map[val] || { color: 'default', label: val };
        return <Tag color={item.color}>{item.label}</Tag>;
      },
    },
    {
      title: 'Options',
      dataIndex: 'options',
      key: 'options',
      render: (opts: CustomAttribute['options']) => (
        <Space size={[4, 4]} wrap>
          {opts.map(o => (
            <Tag key={o.id} color={o.color || undefined}>{o.label}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Required',
      dataIndex: 'isRequired',
      key: 'isRequired',
      width: 80,
      render: (val: boolean) => val ? <Tag color="red">Required</Tag> : <Tag>Optional</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: CustomAttribute) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Delete this attribute?"
            description="This will remove the attribute from all leads and clients."
            onConfirm={() => deleteMutation.mutate(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <TagsOutlined />
            <span>Lead & Client Attributes</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Attribute
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Define custom categories for your leads and clients. These attributes can be used as filters when selecting campaign recipients.
        </Text>

        {attributes.length === 0 && !isLoading ? (
          <Empty
            description="No custom attributes yet"
            style={{ padding: '40px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create Your First Attribute
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={attributes}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingAttr ? 'Edit Attribute' : 'Create Attribute'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSave}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={520}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Attribute Name"
            rules={[{ required: true, message: 'Enter an attribute name' }]}
          >
            <Input placeholder="e.g. Customer Type, Product Interest, Region" />
          </Form.Item>

          <Form.Item name="appliesTo" label="Applies To">
            <Select options={APPLIES_TO_OPTIONS} />
          </Form.Item>

          <Form.Item name="isRequired" label="Required when adding lead/client" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>

        {/* Options */}
        <div style={{ marginBottom: 8 }}>
          <Text strong>Options</Text>
        </div>
        <Space size={[4, 8]} wrap style={{ marginBottom: 12 }}>
          {options.map((opt, idx) => (
            <Tag
              key={idx}
              color={opt.color}
              closable
              onClose={() => removeOption(idx)}
              style={{ fontSize: 13, padding: '2px 8px' }}
            >
              {opt.label}
            </Tag>
          ))}
        </Space>
        <Space.Compact style={{ width: '100%' }}>
          <ColorPicker
            value={newOptionColor}
            onChange={(_, hex) => setNewOptionColor(hex)}
            presets={[{ label: 'Preset', colors: PRESET_COLORS }]}
            size="middle"
          />
          <Input
            placeholder="Type option and press Enter"
            value={newOptionLabel}
            onChange={e => setNewOptionLabel(e.target.value)}
            onPressEnter={addOption}
            style={{ flex: 1 }}
          />
          <Button onClick={addOption} icon={<PlusOutlined />}>
            Add
          </Button>
        </Space.Compact>
      </Modal>
    </div>
  );
};

export default CustomAttributesSettings;
